import { MouseEventHandler, useMemo } from 'react';
import { SidePanelOptionProps } from 'app/shared/components/SidePanel/model';

import { alpha } from '@mui/system/colorManipulator';
import { selectFeatureFlags } from 'app/shared/featureFlags/selectors';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import styled from 'styled-components';

import { ListItemButton, ListItemButtonProps } from '@mui/material';
import { U21Typography } from 'app/shared/u21-ui/components';
import { Link, LinkProps } from 'react-router-dom';

interface Props {
  Elm: SidePanelOptionProps;
  selected: SidePanelOptionProps;
  onClick: MouseEventHandler;
  to?: LinkProps['to'];
}

export const SidePanelMenuItem = (props: Props) => {
  const { Elm, onClick, selected, to } = props;
  const theme = useTheme();
  const featureFlags = useSelector(selectFeatureFlags);

  const hasFlags = useMemo(() => {
    if (Elm.requiredFlags?.length) {
      return Elm.requiredFlags.every((flag) => featureFlags[flag]);
    }
    return true;
  }, [Elm, featureFlags]);

  if (!hasFlags) {
    return null;
  }

  const isSelected = selected.label === Elm.label;

  return (
    <StyledListItemButton
      aria-selected={isSelected}
      onClick={onClick}
      selected={isSelected}
      component={to ? Link : undefined}
      to={to}
    >
      <Elm.MenuIcon
        color={
          isSelected ? theme.palette.primary.main : theme.palette.text.secondary
        }
      />
      <U21Typography
        variant="caption"
        color={isSelected ? 'primary' : 'text.secondary'}
        textAlign="center"
      >
        {Elm.label}
      </U21Typography>
    </StyledListItemButton>
  );
};

const StyledListItemButton = styled(ListItemButton)<{
  // props needed to transform ListItemButton to Link component
  component: ListItemButtonProps['component'];
  to?: LinkProps['to'];
}>`
  &:hover {
    color: ${({ theme }) => theme.palette.text.primary};
  }
  &.Mui-selected {
    background-color: ${({ theme }) => alpha(theme.palette.primary.main, 0.08)};
    border-left: ${({ theme }) => `2px solid ${theme.palette.primary.main}`};
  }
  padding: 8px 2px;
  border-left: 2px solid transparent;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 60px;
`;
